import { Button, ButtonGroup, Card, Combobox, confirmDialog, Field, Input, Tooltip } from "@/components/ui";
import { URLS, useAccountSettings, usePaginatedCombobox } from "@/utils";
import { CategoryServices } from "@/services/bedrock/CategoryServices";
import { Category, ContentPageTheme, Media, VideoScript } from "@/services/bedrock";
import * as React from "react";
import { Controller, useFormContext, type Control } from "react-hook-form";
import { SettingsIcon, LockIcon, UnlockIcon, XIcon, Video, CopyIcon, ExternalLinkIcon } from "lucide-react";
import { ContentPageThemeServices } from "@/services/bedrock/ContentPageThemeServices";
import { SuperPageDetailValues, SuperPageServices } from "@/services/bedrock/SuperPageServices";
import { VideoScriptSelectionDialog } from "@/components";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useHistory } from "react-router-dom";
import { useCallback, useState } from "react";
import { WebComponents } from "@/interfaces";
import { VideoPageSourceModal } from "@/containers/VideoPages";
import VideoPageCTA from "../VideoPages/VideoPageModal/EditVideoPageCard/VideoPageCTA";

function fetchCategories(_search: string | undefined, page: number) {
  return CategoryServices.getCategories({
    type: "SUPER_PAGE",
    excludeUnusedCategories: true,
    page,
    perPage: 100,
  });
}

function fetchPageThemes(_search: string | undefined, page: number) {
  return ContentPageThemeServices.getPageThemes({ page, perPage: 25 });
}

type LockableFieldLabelProps = {
  control: Control<SuperPageDetailValues>;
  lockFieldName: "allowTitleChange" | "allowCtaChange" | "allowThemeChange";
  label: string;
  disabled?: boolean;
};

function LockableFieldLabel({ control, lockFieldName, label, disabled }: LockableFieldLabelProps) {
  return (
    <div className="flex items-center gap-2">
      <div>{label}</div>
      <Controller
        name={lockFieldName}
        control={control}
        render={({ field }) => (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  type="button"
                  size="icon-sm"
                  variant={field.value ? "outline" : "default"}
                  disabled={disabled}
                  onClick={() => field.onChange(!field.value)}
                  aria-label={field.value ? `Lock ${label}` : `Unlock ${label}`}
                >
                  {field.value ? <UnlockIcon /> : <LockIcon />}
                </Button>
              }
            />
            <Tooltip.Content>
              {field.value ? `Lock the ${label} to prevent changes` : `Unlock to allow ${label} changes`}
            </Tooltip.Content>
          </Tooltip>
        )}
      />
    </div>
  );
}

const SuperPageDetailsForm: React.FC = () => {
  const [videoScriptModalOpen, setVideoScriptModalOpen] = React.useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [sourceModalVideoScript, setSourceModalVideoScript] = useState<VideoScript | null>();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useFormContext<SuperPageDetailValues>();

  const videoScript = watch("videoScript");
  const categories = watch("categories");
  const contentPageTheme = watch("contentPageTheme");
  const pageUrl = watch("url");
  const superPageId = watch("id");
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.VIDEO_PAGES);

  const categoriesCombobox = usePaginatedCombobox<Category>(fetchCategories, true, categories, (query: string) => ({
    id: -new Date().getTime(),
    coachId: -1,
    type: "SUPER_PAGE",
    name: query,
  }));
  const pageThemesCombobox = usePaginatedCombobox<ContentPageTheme>(fetchPageThemes, false, contentPageTheme);

  const history = useHistory();
  const handleDeleteClick = useCallback(() => {
    if (!superPageId) return;
    confirmDialog({
      title: "Delete Super Page",
      description: "Are you sure you want to delete this Super Page?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    }).then(result => {
      if (!result) return;
      SuperPageServices.deleteSuperPage(superPageId)
        .then(() => {
          history.push(URLS.coach.superPages);
        })
        .catch(error => {
          console.log(error);
          toast.error("Error deleting Super Page");
        });
    });
  }, [superPageId, history]);

  const handleSourceModalOpenChange = useCallback((open: boolean) => {
    setShowSourceModal(open);
    if (!open) {
      setSourceModalVideoScript(undefined);
    }
  }, []);

  const handleMediaSelected = useCallback(
    (media: Media) => {
      setValue("originalMedia", [media], { shouldDirty: true });
      if (sourceModalVideoScript !== undefined) {
        setValue("videoScript", sourceModalVideoScript, { shouldDirty: true });
      }
      setSourceModalVideoScript(undefined);
      setShowSourceModal(false);
    },
    [sourceModalVideoScript, setValue]
  );

  const pageStatus = () => {
    if (!superPageId) return null;
    return (
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <Field name="status" label="Page Status">
            <div className="flex items-center justify-between gap-4 bg-gray-50/50 p-3 rounded-lg border border-gray-100 w-full">
              <ButtonGroup>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    field.value === "DRAFT"
                      ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                      : ""
                  )}
                  disabled={isSubmitting}
                  onClick={() => field.onChange("DRAFT")}
                  aria-pressed={field.value === "DRAFT"}
                >
                  Draft
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    field.value === "ACTIVE"
                      ? "bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
                      : ""
                  )}
                  disabled={isSubmitting}
                  onClick={() => field.onChange("ACTIVE")}
                  aria-pressed={field.value === "ACTIVE"}
                >
                  Published
                </Button>
              </ButtonGroup>

              {pageUrl && (
                <div className="flex items-center gap-2 overflow-hidden">
                  <a
                    href={pageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline truncate max-w-[250px] flex items-center gap-1"
                    title="Open in new tab"
                  >
                    <span className="truncate">{pageUrl}</span>
                    <ExternalLinkIcon className="size-3 shrink-0" />
                  </a>

                  <Tooltip>
                    <Tooltip.Trigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(pageUrl);
                            toast.success("Link copied to clipboard");
                          }}
                          aria-label="Copy link"
                        >
                          <CopyIcon className="size-4 text-gray-500" />
                        </Button>
                      }
                    />
                    <Tooltip.Content>Copy link</Tooltip.Content>
                  </Tooltip>
                </div>
              )}
            </div>
          </Field>
        )}
      />
    );
  };

  return (
    <>
      <VideoScriptSelectionDialog
        open={videoScriptModalOpen}
        onOpenChange={setVideoScriptModalOpen}
        defaultSelectedItems={videoScript ? [videoScript] : []}
        title="Select a Video Script"
        description="Select a Video Script to use for your Super Page."
        onSelect={script => {
          setValue("videoScript", script, { shouldDirty: true });
          setVideoScriptModalOpen(false);
        }}
      />
      <VideoPageSourceModal
        open={showSourceModal}
        title="Edit Video Page"
        videoScript={sourceModalVideoScript ?? videoScript}
        onVideoScriptSelected={setSourceModalVideoScript}
        onOpenChange={handleSourceModalOpenChange}
        onMediaSelected={handleMediaSelected}
      />

      <Field.Group className="w-xl min-w-md">
        {pageStatus()}

        <Field label="Super Page Name" name="title" error={errors.title?.message}>
          {props => (
            <Input
              disabled={isSubmitting || !withWritePermission}
              {...props}
              {...register("title", {
                required: "Name is required",
                validate: value => (value?.trim()?.length ?? 0) > 0 || "Name is required",
              })}
              placeholder="Enter name"
              className="w-full"
            />
          )}
        </Field>

        <Controller
          name="categories"
          control={control}
          render={({ field }) => (
            <Field label="Categories" name="categories">
              <Combobox
                {...categoriesCombobox}
                disabled={isSubmitting || !withWritePermission}
                onValueChange={(val, _eventDetails) => {
                  field.onChange(val ?? []);
                  categoriesCombobox.onValueChange(val, _eventDetails);
                }}
                itemToStringLabel={item => item.name}
                placeholder="Select categories..."
                showClear
                className="w-full"
              />
            </Field>
          )}
        />

        <Controller
          name="videoScript"
          control={control}
          render={({ field }) => (
            <Field label="Video Script" name="videoScript">
              {field.value ? (
                <Card
                  className="w-full cursor-pointer hover:shadow-md"
                  disabled={isSubmitting || !withWritePermission}
                  onClick={() => setVideoScriptModalOpen(true)}
                >
                  <Card.Header className="flex justify-between items-center gap-2">
                    <Card.Title className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {field.value.title}
                    </Card.Title>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={e => {
                        e.stopPropagation();
                        setValue("videoScript", null, { shouldDirty: true });
                      }}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </Card.Header>
                  <Card.Content>
                    <Card.Description className="overflow-hidden text-ellipsis line-clamp-3">
                      {field.value.content}
                    </Card.Description>
                  </Card.Content>
                </Card>
              ) : (
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting || !withWritePermission}
                    className="w-40"
                    tabIndex={0}
                    onClick={() => setVideoScriptModalOpen(true)}
                  >
                    Select a Script
                  </Button>
                </div>
              )}
            </Field>
          )}
        />

        <Controller
          name="originalMedia"
          control={control}
          render={({ field }) => (
            <Field label="Default Video" name="originalMedia" error={errors.originalMedia?.message}>
              {!field.value?.[0] ? (
                <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors gap-4 w-full">
                  <div className="bg-white p-3 rounded-full shadow-sm border border-gray-100">
                    <Video className="text-gray-400 size-6" />
                  </div>
                  <div className="text-sm text-gray-500 text-center max-w-xs">
                    Upload a pre-recorded video or record a new one directly from your browser.
                  </div>
                  <div className="flex gap-3 mt-2">
                    <Button
                      variant="outline"
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => setShowSourceModal(true)}
                    >
                      Add Video
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col w-max self-start">
                  <div className="relative w-fit inline-block">
                    <img
                      src={field.value[0].thumbnailUri}
                      className="h-48 min-w-24 w-auto object-contain rounded-xl shadow-sm border border-gray-200 bg-black/5"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className="absolute top-2 right-2 bg-white shadow-md border border-gray-200 rounded-full hover:bg-red-50 hover:text-red-600 hover:border-red-200 size-8 min-h-0 min-w-0 flex items-center justify-center p-0 transition-colors"
                      onClick={() => setValue("originalMedia", null, { shouldDirty: true })}
                      aria-label="Remove video"
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          )}
        />

        <Controller
          name="pageTitle"
          control={control}
          render={({ field }) => (
            <Field
              name="pageTitle"
              error={errors.pageTitle?.message}
              label={
                <LockableFieldLabel
                  control={control}
                  lockFieldName="allowTitleChange"
                  label="Title"
                  disabled={isSubmitting || !withWritePermission}
                />
              }
            >
              <Input disabled={isSubmitting} {...field} placeholder="Enter title" className="w-full" />
            </Field>
          )}
        />

        <VideoPageCTA disabled={isSubmitting || !withWritePermission} />

        <Controller
          name="contentPageTheme"
          control={control}
          render={({ field }) => (
            <Field
              name="contentPageTheme"
              label={
                <LockableFieldLabel
                  control={control}
                  lockFieldName="allowThemeChange"
                  label="Page Theme"
                  disabled={isSubmitting || !withWritePermission}
                />
              }
            >
              <div className="flex items-center gap-2">
                <Combobox
                  {...pageThemesCombobox}
                  disabled={isSubmitting || !withWritePermission}
                  onValueChange={(val, _eventDetails) => {
                    field.onChange(val);
                    pageThemesCombobox.onValueChange(val, _eventDetails);
                  }}
                  itemToStringLabel={item => item.name}
                  placeholder="Select theme..."
                  showClear
                  className="flex-auto"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  children={<SettingsIcon className="size-4" />}
                  onClick={() => window.open(URLS.coach.mvpSettings, "_blank")}
                />
              </div>
            </Field>
          )}
        />
        {superPageId && withWritePermission && (
          <div className="flex flex-row gap-2 self-center">
            <Button
              type="button"
              variant="destructiveGhost"
              children="Delete"
              disabled={isSubmitting}
              onClick={handleDeleteClick}
            />
            <Button type="submit" children="Save" disabled={isSubmitting || !isDirty} />
          </div>
        )}
      </Field.Group>
    </>
  );
};

export { SuperPageDetailsForm };
